/**
 * Widget d'affichage des statistiques par devise pour les propriétaires
 * Affiche les statistiques détaillées d'une devise spécifique avec accordéon rétractable
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Button,
  Collapse,
  Avatar,
  Paper,
  IconButton,
  Skeleton,
  LinearProgress,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as StatisticsIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  SwapHoriz as TransactionIcon,
  LocalAtm as DepositIcon,
  Public as GlobalIcon,
  CurrencyExchange as CurrencyIcon,
  DateRange as DateRangeIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { formatPrice } from '../../config/currencies';
import { formatDate, formatDateTime } from '../../utils/formatters';
import { aggregateAllCurrenciesStats } from '../../utils/currencyStatsAggregator';
import OwnerGlobalCurrencyStatsView from './components/OwnerGlobalCurrencyStatsView';
import * as apiClient from '../../utils/apiClient';

const OwnerCurrencyStatisticsWidget = () => {
  const { t } = useTranslation('financial');
  const { user } = useAuth();
  
  // États principaux
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null); // Sera défini dynamiquement
  
  // États pour la vue globale
  const [viewMode, setViewMode] = useState('currency'); // 'currency' ou 'global'
  const [globalStats, setGlobalStats] = useState(null);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  
  // État pour la liste déroulante combinée
  const [selectedView, setSelectedView] = useState('global'); // 'global' ou code devise (ex: 'EUR')
  
  // États pour les filtres de date
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Gestion de l'expansion
  const handleToggle = () => {
    setExpanded(!expanded);
  };

  // Gestion des filtres de date
  const handleDateFromChange = (event) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event) => {
    setDateTo(event.target.value);
  };

  const handleClearFilters = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Chargement des devises disponibles
  const loadAvailableCurrencies = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoadingCurrencies(true);
      
      console.log('Loading available currencies for owner');
      
      const response = await apiClient.get('/financial/owner/available-currencies');
      
      if (response.success && response.data) {
        setAvailableCurrencies(response.data);
        
        // Par défaut, commencer avec la vue globale
        if (selectedView === 'global') {
          setViewMode('global');
          setSelectedCurrency(null);
        } else {
          // Ou sélectionner automatiquement la première devise avec activité
          const defaultCurrency = response.data.find(c => c.has_activity)?.currency || response.data[0]?.currency;
          if (defaultCurrency && selectedView !== 'global') {
            setSelectedCurrency(defaultCurrency);
            setSelectedView(defaultCurrency);
            setViewMode('currency');
          }
        }
        
        console.log('Available currencies loaded:', response.data);
      } else {
        throw new Error('Données de devises invalides');
      }
      
    } catch (err) {
      console.error('Error loading available currencies:', err);
      // En cas d'erreur, utiliser des devises par défaut
      const defaultCurrencies = [
        { currency: 'EUR', has_activity: true, reservations_count: 0 },
        { currency: 'USD', has_activity: false, reservations_count: 0 },
        { currency: 'DZD', has_activity: false, reservations_count: 0 }
      ];
      setAvailableCurrencies(defaultCurrencies);
      if (!selectedCurrency) {
        setSelectedCurrency('EUR');
      }
    } finally {
      setLoadingCurrencies(false);
    }
  }, [user, selectedCurrency]);

  // Chargement des statistiques
  const loadStatistics = useCallback(async (currency = 'EUR') => {
    if (!currency || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading currency statistics:', currency);
      
      // Construire les paramètres de requête avec les filtres de date
      const params = new URLSearchParams();
      if (dateFrom) {
        params.append('period_start', dateFrom);
      }
      if (dateTo) {
        params.append('period_end', dateTo);
      }
      
      const queryString = params.toString();
      const url = `/financial/owner/currency-statistics/${currency}${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get(url);
      
      if (response.success && response.data) {
        setStatistics(response.data);
        console.log('Currency statistics loaded:', response.data);
      } else {
        throw new Error('Données de statistiques invalides');
      }
      
    } catch (err) {
      console.error('Error loading currency statistics:', err);
      setError(err.response?.data?.message || err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user, dateFrom, dateTo]);

  // Effet de chargement des devises disponibles
  useEffect(() => {
    if (user) {
      loadAvailableCurrencies();
    }
  }, [user, loadAvailableCurrencies]);

  // Effet de chargement initial
  useEffect(() => {
    if (user && selectedCurrency) {
      loadStatistics(selectedCurrency);
    }
  }, [user, selectedCurrency, loadStatistics]);

  // Effet pour recharger les données quand les filtres de date changent
  useEffect(() => {
    if (user && expanded) {
      if (viewMode === 'currency' && selectedCurrency) {
        loadStatistics(selectedCurrency);
      } else if (viewMode === 'global' && availableCurrencies.length > 0) {
        loadGlobalStatistics();
      }
    }
  }, [dateFrom, dateTo]);

  // Fonction de rafraîchissement
  const handleRefresh = () => {
    if (viewMode === 'currency') {
      loadStatistics(selectedCurrency);
    } else {
      loadGlobalStatistics();
    }
  };

  // Fonction de changement de devise
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    loadStatistics(currency);
  };

  // Fonction de chargement des statistiques globales
  const loadGlobalStatistics = useCallback(async () => {
    if (!availableCurrencies.length) {
      setGlobalError('Aucune devise disponible pour l\'agrégation');
      return;
    }

    try {
      setLoadingGlobal(true);
      setGlobalError(null);
      
      console.log('🌍 Chargement des statistiques globales pour', availableCurrencies.length, 'devises');

      // Fonction pour récupérer les statistiques d'une devise avec filtres de date
      const getStatisticsForCurrency = async (currency) => {
        // Construire les paramètres de requête avec les filtres de date
        const params = new URLSearchParams();
        if (dateFrom) {
          params.append('period_start', dateFrom);
        }
        if (dateTo) {
          params.append('period_end', dateTo);
        }
        
        const queryString = params.toString();
        const url = `/financial/owner/currency-statistics/${currency}${queryString ? `?${queryString}` : ''}`;
        
        const response = await apiClient.get(url);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(`Erreur pour ${currency}: ${response.message || 'Données invalides'}`);
      };

      // Agrégation de toutes les devises
      const aggregatedStats = await aggregateAllCurrenciesStats(
        availableCurrencies, 
        getStatisticsForCurrency
      );

      setGlobalStats(aggregatedStats);
      console.log('✅ Statistiques globales chargées:', aggregatedStats);

    } catch (error) {
      console.error('❌ Erreur lors du chargement des statistiques globales:', error);
      setGlobalError(error.message || 'Erreur lors de l\'agrégation des statistiques');
    } finally {
      setLoadingGlobal(false);
    }
  }, [availableCurrencies, dateFrom, dateTo]);

  // Fonction de basculement entre les vues
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'global' && !globalStats) {
      loadGlobalStatistics();
    }
  };

  // Effet pour charger les stats globales si on bascule vers cette vue
  useEffect(() => {
    if (viewMode === 'global' && availableCurrencies.length > 0 && !globalStats) {
      loadGlobalStatistics();
    }
  }, [viewMode, availableCurrencies, globalStats, loadGlobalStatistics]);

  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status) => {
    const iconProps = { fontSize: 'small' };
    
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'completed':
      case 'paid':
        return <CheckCircleIcon color="success" {...iconProps} />;
      case 'pending':
      case 'processing':
        return <PendingIcon color="warning" {...iconProps} />;
      case 'cancelled':
      case 'failed':
      case 'refunded':
        return <CancelIcon color="error" {...iconProps} />;
      default:
        return <ScheduleIcon color="info" {...iconProps} />;
    }
  };

  // Fonction pour traduire les libellés de statut
  const translateStatus = (status, type = 'payment') => {
    if (!status) return '';
    const key = type === 'reservation'
      ? `reservationStatuses.${status}`
      : `payment_status.${status}`;
    const result = t(key);
    // If not found, i18next returns the key path (contains '.') — fall back to raw
    return result.includes('.') ? status : result;
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'cancelled':
      case 'failed':
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  // Rendu des statistiques de réservations
  const renderReservationsStats = () => {
    if (!statistics?.reservations_statistics) return null;
    
    const stats = statistics.reservations_statistics;
    
    // Vérification défensive - s'assurer que by_status est un tableau
    const statusData = Array.isArray(stats.by_status) ? stats.by_status : [];
    
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32, mr: 1 }}>
            <AccountBalanceIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" color="primary">
            {t('currencyStats.reservationsSection', { count: stats.total_count })}
          </Typography>
        </Box>
        
        {statusData.length === 0 ? (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            p={2}
            bgcolor="grey.50"
            borderRadius={1}
          >
            <Typography variant="body2" color="text.secondary">
              {t('currencyStats.noReservations')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {statusData.map((statusItem) => (
              <Grid item xs={6} md={3} key={statusItem.status}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                  p={1}
                  borderRadius={1}
                  bgcolor="grey.50"
                >
                  <Box display="flex" alignItems="center">
                    {getStatusIcon(statusItem.status)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {translateStatus(statusItem.status, 'reservation')}
                    </Typography>
                  </Box>
                  <Chip 
                    label={statusItem.count}
                    size="small"
                    color={getStatusColor(statusItem.status)}
                    variant="outlined"
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    );
  };

  // Rendu des statistiques de transactions
  const renderTransactionsStats = () => {
    if (!statistics?.transactions_statistics) return null;
    
    const stats = statistics.transactions_statistics;
    
    // Vérification défensive - s'assurer que by_status est un tableau
    const statusData = Array.isArray(stats.by_status) ? stats.by_status : [];
    
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: '#388e3c', width: 32, height: 32, mr: 1 }}>
            <TransactionIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" color="success.main">
            {t('currencyStats.transactionsSection', { count: stats.total_count })}
          </Typography>
        </Box>
        
        {statusData.length === 0 ? (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            p={2}
            bgcolor="grey.50"
            borderRadius={1}
          >
            <Typography variant="body2" color="text.secondary">
              {t('currencyStats.noTransactions')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {statusData.map((statusItem) => (
              <Grid item xs={6} md={3} key={statusItem.status}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                  p={1}
                  borderRadius={1}
                  bgcolor="grey.50"
                >
                  <Box display="flex" alignItems="center">
                    {getStatusIcon(statusItem.status)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {translateStatus(statusItem.status)}
                    </Typography>
                  </Box>
                  <Chip 
                    label={statusItem.count}
                    size="small"
                    color={getStatusColor(statusItem.status)}
                    variant="outlined"
                  />
                </Box>
              </Grid>
            ))}
            
            {stats.largest_transaction && stats.largest_transaction > 0 && (
              <Grid item xs={12}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                  p={1}
                  borderRadius={1}
                  bgcolor="success.50"
                  border="1px solid"
                  borderColor="success.200"
                >
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="success.main">
                    {t('currencyStats.largestTransaction')} <strong>{formatPrice(stats.largest_transaction, selectedCurrency)}</strong>
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>
    );
  };

  // Rendu des statistiques de dépôts
  const renderDepositsStats = () => {
    if (!statistics?.deposits_statistics) return null;
    
    const stats = statistics.deposits_statistics;
    
    // Vérification défensive - s'assurer que by_status est un tableau
    const statusData = Array.isArray(stats.by_status) ? stats.by_status : [];
    
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: '#f57c00', width: 32, height: 32, mr: 1 }}>
            <DepositIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" color="warning.main">
            {t('currencyStats.depositsSection', { count: stats.total_count })}
          </Typography>
        </Box>
        
        {statusData.length === 0 ? (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            p={2}
            bgcolor="grey.50"
            borderRadius={1}
          >
            <Typography variant="body2" color="text.secondary">
              {t('currencyStats.noDeposits')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {statusData.map((statusItem) => (
              <Grid item xs={6} md={3} key={statusItem.status}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                  p={1}
                  borderRadius={1}
                  bgcolor="grey.50"
                >
                  <Box display="flex" alignItems="center">
                    {getStatusIcon(statusItem.status)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {translateStatus(statusItem.status)}
                    </Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="end">
                    <Chip 
                      label={statusItem.count}
                      size="small"
                      color={getStatusColor(statusItem.status)}
                      variant="outlined"
                    />
                    {statusItem.total_amount && statusItem.total_amount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {formatPrice(statusItem.total_amount, selectedCurrency)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    );
  };

  // Rendu de la période d'activité
  const renderActivityPeriod = () => {
    if (!statistics?.activity_period) return null;
    
    const period = statistics.activity_period;
    
    if (!period.has_activity) {
      return (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <CalendarIcon color="disabled" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('currencyStats.noActivity', { currency: selectedCurrency })}
            </Typography>
          </Box>
        </Paper>
      );
    }
    
    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32, mr: 1 }}>
            <CalendarIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" color="primary">
            {t('currencyStats.activityPeriod')}
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box 
              display="flex" 
              alignItems="center" 
              p={1}
              borderRadius={1}
              bgcolor="grey.50"
            >
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                {t('currencyStats.activityStart')}
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {period.first_transaction ? formatDate(period.first_transaction) : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box 
              display="flex" 
              alignItems="center" 
              p={1}
              borderRadius={1}
              bgcolor="grey.50"
            >
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                {t('currencyStats.activityEnd')}
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {period.last_transaction ? formatDate(period.last_transaction) : 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Sélecteur de devise et de mode de vue - Liste déroulante élégante
  const renderCurrencySelector = () => {
    if (loadingCurrencies) {
      return (
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t('currencyStats.loadingCurrencies')}
          </Typography>
        </Box>
      );
    }

    if (!availableCurrencies.length) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('currencyStats.noCurrencies')}
          </Typography>
        </Alert>
      );
    }

    // Gestionnaire de changement de sélection dans la liste
    const handleSelectChange = (event) => {
      const value = event.target.value;
      setSelectedView(value);
      
      if (value === 'global') {
        // Mode vue globale
        setViewMode('global');
        setSelectedCurrency(null);
        loadGlobalStatistics();
      } else {
        // Mode devise spécifique
        setViewMode('currency');
        setSelectedCurrency(value);
        // Le useEffect se chargera de loadCurrencyStatistics
      }
    };

    // Préparer les options de la liste
    const getSelectOptions = () => {
      const options = [];
      
      // Ajouter la vue globale en premier
      options.push(
        <MenuItem key="global" value="global">
          <Box display="flex" alignItems="center" gap={2}>
            <GlobalIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {t('currencyStats.globalOption')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('currencyStats.globalOptionSubtitle', { count: availableCurrencies.length })}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      );
      
      // Ajouter un divider
      options.push(<Divider key="divider" />);
      
      // Ajouter les devises disponibles
      availableCurrencies.forEach((currencyData) => {
        options.push(
          <MenuItem key={currencyData.currency} value={currencyData.currency}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              <CurrencyIcon sx={{ 
                color: currencyData.has_activity ? '#1976d2' : '#9e9e9e',
                fontSize: 20 
              }} />
              <Box flex={1}>
                <Typography 
                  variant="body2" 
                  fontWeight="medium"
                  color={currencyData.has_activity ? 'text.primary' : 'text.secondary'}
                >
                  {t('currencyStats.currencyOption', { currency: currencyData.currency })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('currencyStats.currencyOptionSubtitle', { reservations: currencyData.reservations_count, payments: currencyData.payments_count })}
                  {currencyData.completed_reservations > 0 && ` ${t('currencyStats.currencyOptionCompleted', { completed: currencyData.completed_reservations })}`}
                </Typography>
              </Box>
              {currencyData.has_activity && (
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
              )}
            </Box>
          </MenuItem>
        );
      });
      
      return options;
    };

    return (
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl 
            fullWidth 
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#9c27b0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9c27b0',
                  borderWidth: '2px',
                },
              },
            }}
          >
            <Select
              value={selectedView}
              onChange={handleSelectChange}
              displayEmpty
              sx={{
                minHeight: 56,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    '& .MuiMenuItem-root': {
                      py: 1.5,
                      borderRadius: 1,
                      mx: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(156, 39, 176, 0.08)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(156, 39, 176, 0.12)',
                        '&:hover': {
                          backgroundColor: 'rgba(156, 39, 176, 0.16)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {getSelectOptions()}
            </Select>
          </FormControl>
          
          <Tooltip title={t('currencyStats.tooltipRefresh')}>
            <IconButton 
              size="large" 
              onClick={handleRefresh} 
              disabled={loading || loadingGlobal}
              sx={{
                bgcolor: 'rgba(156, 39, 176, 0.08)',
                border: '1px solid rgba(156, 39, 176, 0.2)',
                '&:hover': {
                  bgcolor: 'rgba(156, 39, 176, 0.12)',
                },
              }}
            >
              <StatisticsIcon sx={{ color: '#9c27b0' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* En-tête avec gradient et contrôles */}
      <Paper 
        elevation={2}
        sx={{
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
          },
          p: 2,
          borderRadius: '12px 12px 0 0',
          cursor: 'pointer'
        }}
        onClick={handleToggle}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
              <StatisticsIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {t('currencyStats.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {viewMode === 'global' ? 
                  t('currencyStats.viewGlobal', { count: availableCurrencies.length }) : 
                  (selectedCurrency ? t('currencyStats.viewDetailed', { currency: selectedCurrency }) : t('currencyStats.selectCurrency'))
                }
                {(dateFrom || dateTo) && (
                  <Box component="span" sx={{ ml: 1, opacity: 0.8 }}>
                    • {t('currencyStats.filteredBadge')}
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={t('currencyStats.tooltipFilterDate')}>
              <IconButton 
                sx={{ 
                  color: 'white',
                  bgcolor: showFilters ? 'rgba(255,255,255,0.2)' : 'transparent'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFilters();
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <IconButton sx={{ color: 'white' }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Section des filtres de date */}
      <Collapse in={showFilters && expanded}>
        <Paper 
          elevation={1}
          sx={{
            borderRadius: 0,
            border: '1px solid #e0e0e0',
            borderTop: 'none',
            borderBottom: 'none'
          }}
        >
          <CardContent sx={{ p: 2, bgcolor: '#f8f9fa' }}>
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DateRangeIcon color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('currencyStats.filterByPeriod')}
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  type="date"
                  label={t('currencyStats.dateFrom')}
                  value={dateFrom || ''}
                  onChange={handleDateFromChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
                
                <TextField
                  type="date"
                  label={t('currencyStats.dateTo')}
                  value={dateTo || ''}
                  onChange={handleDateToChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
                
                <Tooltip title={t('currencyStats.tooltipClearFilters')}>
                  <IconButton 
                    onClick={handleClearFilters}
                    disabled={!dateFrom && !dateTo}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
                
                {(dateFrom || dateTo) && (
                  <Chip
                    label={`${dateFrom ? t('currencyStats.dateChipFrom', { date: formatDate(dateFrom) }) : ''} ${dateTo ? t('currencyStats.dateChipTo', { date: formatDate(dateTo) }) : ''}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onDelete={handleClearFilters}
                  />
                )}
              </Stack>
              
              {(dateFrom || dateTo) && (
                <Typography variant="caption" color="text.secondary">
                  {t('currencyStats.filterHint')}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Paper>
      </Collapse>

      {/* Contenu rétractable */}
      <Collapse in={expanded}>
        <Paper 
          elevation={2}
          sx={{
            borderRadius: '0 0 12px 12px',
            border: '1px solid #e0e0e0',
            borderTop: 'none'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Sélecteur de devise et mode */}
            {renderCurrencySelector()}
            
            {/* Contenu conditionnel selon le mode de vue */}
            {viewMode === 'global' ? (
              /* Vue globale */
              <OwnerGlobalCurrencyStatsView 
                globalStats={globalStats}
                loading={loadingGlobal}
                error={globalError}
                onRefresh={loadGlobalStatistics}
              />
            ) : (
              /* Vue par devise */
              <Box>
                {/* Message si aucune devise sélectionnée */}
                {!selectedCurrency && !loadingCurrencies && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      {t('currencyStats.selectCurrencyHint')}
                    </Typography>
                  </Alert>
                )}
                
                {/* États de chargement et d'erreur pour la vue par devise */}
                {loading && (
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      {t('currencyStats.loadingStats', { currency: selectedCurrency })}
                    </Typography>
                  </Box>
                )}
                
                {error && !loading && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3 }}
                    action={
                      <Button size="small" onClick={handleRefresh}>
                        {t('currencyStats.retry')}
                      </Button>
                    }
                  >
                    {error}
                  </Alert>
                )}
                
                {/* Affichage des statistiques par devise */}
                {statistics && !loading && selectedCurrency && (
                  <Box>
                    {renderReservationsStats()}
                    {renderTransactionsStats()}
                    {renderDepositsStats()}
                    {renderActivityPeriod()}
                    
                    {/* Pied de page avec mise à jour */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                      mt={2}
                      pt={2}
                      borderTop="1px solid"
                      borderColor="grey.200"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t('currencyStats.lastUpdate')} {formatDateTime(new Date())}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {/* Message si pas de données pour la devise */}
                {!statistics && !loading && !error && selectedCurrency && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      {t('currencyStats.noStatsForCurrency', { currency: selectedCurrency })}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default OwnerCurrencyStatisticsWidget;